import { Card, CardContent } from "@/components/ui/card";
import { Target, Heart, Lightbulb, Users } from "lucide-react";
import teamImage from "@/assets/team-collaboration.jpg";
import automationImage from "@/assets/automation-workflow.jpg";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Human-Centered AI",
      description: "We believe AI should enhance human capabilities, not replace them. Our solutions are designed to free people to focus on meaningful work.",
    },
    {
      icon: Lightbulb,
      title: "Innovation with Purpose",
      description: "Every solution we create is driven by real business needs and designed to deliver measurable results.",
    },
    {
      icon: Users,
      title: "Partnership Approach",
      description: "We work closely with our clients to understand their unique challenges and create tailored solutions.",
    },
    {
      icon: Target,
      title: "Results-Driven",
      description: "Our success is measured by the efficiency gains and growth our clients achieve with our AI Virtual Employees.",
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4 md:px-6">
        {/* Hero Section */}
        <section className="mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">
            About Voice Vision Media
          </h1>
          <div className="h-1 w-24 bg-accent mb-8"></div>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl animate-fade-in">
            Voice Vision Media is an AI technology company specializing in AI Virtual Employees that empower small businesses, healthcare providers, and skilled trade companies to scale efficiently and intelligently.
          </p>
        </section>

        {/* Mission Section */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center bg-secondary/30 rounded-2xl overflow-hidden">
            <div className="p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg text-muted-foreground mb-6">
                We help organizations transform their operations by integrating intelligent, human-like virtual assistants that handle customer service, scheduling, communication, and workflow automation. Our mission is to make advanced AI technology accessible and practical for businesses of all sizes.
              </p>
              <p className="text-lg text-muted-foreground">
                By combining cutting-edge AI with a deep understanding of business operations, we create solutions that don't just automate tasks—they enhance entire workflows, improve customer experiences, and drive sustainable growth.
              </p>
            </div>
            <div className="h-full min-h-[300px]">
              <img
                src={teamImage}
                alt="Team Collaboration with AI"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Our Founder</h2>
          <Card className="border-2">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Caleb Santiago</h3>
              <p className="text-lg text-muted-foreground mb-4">
                Caleb Santiago founded Voice Vision Media with a vision to bridge the gap between advanced AI technology and practical business applications. With a background in both technology and business operations, Caleb recognized that many organizations struggle to leverage AI effectively due to complexity and implementation challenges.
              </p>
              <p className="text-lg text-muted-foreground">
                His approach combines technical expertise with a human-centered philosophy, ensuring that every AI solution enhances rather than replaces the human touch that makes businesses successful. Under his leadership, Voice Vision Media has helped numerous organizations transform their operations while maintaining the personal connections that matter most to their customers.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Values Section */}
        <section className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Our Values</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-2 hover:border-accent transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <value.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Additional Image Section */}
        <section className="rounded-2xl overflow-hidden">
          <img
            src={automationImage}
            alt="AI Automation Workflow"
            className="w-full h-[400px] object-cover"
          />
        </section>
      </div>
    </div>
  );
};

export default About;
